import React, { useState, useEffect } from 'react';
import { getPollinationsDailyUsage } from '@/services/pollinations';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, PieChart, Pie
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Activity, CircleDollarSign, Target, Calendar } from 'lucide-react';

const COLORS = ['#16a34a', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];

const PollinationsDailyUsage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDaily = async () => {
      try {
        const { data: apiData, error: apiError } = await getPollinationsDailyUsage({ days: 30 });
        if (apiError) {
          setError(apiError);
        } else {
          // Robust array extraction
          const cleanData = Array.isArray(apiData) ? apiData : (apiData?.data || apiData?.usage || []);
          setData(cleanData);
        }
      } catch (err) {
        setError("Failed to load analytics");
      } finally {
        setLoading(false);
      }
    };
    fetchDaily();
  }, []);

  const chartData = React.useMemo(() => {
    if (!Array.isArray(data) || !data.length) return [];
    
    const aggregatedByDate = data.reduce((acc, curr) => {
      if (!curr || !curr.date) return acc;
      const date = curr.date;
      if (!acc[date]) acc[date] = { date, requests: 0, cost: 0 };
      acc[date].requests += (curr.requests || 0);
      acc[date].cost += (curr.cost || 0);
      return acc;
    }, {});

    return Object.values(aggregatedByDate).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [data]);

  const modelUsage = React.useMemo(() => {
    if (!Array.isArray(data) || !data.length) return [];
    
    const aggregatedByModel = data.reduce((acc, curr) => {
      if (!curr || !curr.model) return acc;
      const model = curr.model;
      if (!acc[model]) acc[model] = { name: model, value: 0 };
      acc[model].value += (curr.requests || 0);
      return acc;
    }, {});

    return Object.values(aggregatedByModel).sort((a, b) => b.value - a.value);
  }, [data]);

  if (loading) return <div className="space-y-6 p-10"><Skeleton className="h-96 w-full rounded-3xl bg-white/5" /></div>;

  if (error) {
    return (
      <div className="p-20 text-center space-y-4">
        <Activity className="h-12 w-12 text-red-500 mx-auto opacity-50" />
        <h3 className="text-xl font-syne font-bold text-red-500">{error}</h3>
        <p className="text-sonas-muted text-sm">Provider API reported an issue or returned incompatible data.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <section>
        <h1 className="text-2xl font-syne font-bold">Daily Intelligence</h1>
        <p className="text-sonas-muted text-xs mt-1">Aggregated consumption and economic performance (30d).</p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-sonas-surface border-sonas-border rounded-3xl p-6 shadow-sm">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim mb-8 flex items-center gap-2">
            <Activity size={14} className="text-sonas-accent" /> Request Volume
          </h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                <XAxis dataKey="date" stroke="#444" fontSize={9} tickLine={false} axisLine={false} tickFormatter={(v) => v.split('-').slice(1).join('/')} />
                <YAxis stroke="#444" fontSize={9} tickLine={false} axisLine={false} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '12px' }}
                />
                <Area type="monotone" dataKey="requests" stroke="#16a34a" fillOpacity={1} fill="url(#colorRequests)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="bg-sonas-surface border-sonas-border rounded-3xl p-6 shadow-sm">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim mb-8 flex items-center gap-2">
            <CircleDollarSign size={14} className="text-blue-500" /> Economic Burn
          </h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                <XAxis dataKey="date" stroke="#444" fontSize={9} tickLine={false} axisLine={false} tickFormatter={(v) => v.split('-').slice(1).join('/')} />
                <YAxis stroke="#444" fontSize={9} tickLine={false} axisLine={false} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '12px' }}
                  formatter={(v) => [`$${v.toFixed(3)}`, 'Cost']}
                />
                <Bar dataKey="cost" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 bg-sonas-surface border-sonas-border rounded-3xl p-6 shadow-sm">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim mb-8">Model Mix</h3>
          <div className="h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={modelUsage}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {modelUsage.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {modelUsage.slice(0, 4).map((m, i) => (
              <div key={m.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                  <span className="text-[10px] font-bold uppercase text-sonas-muted">{m.name}</span>
                </div>
                <span className="text-[10px] font-mono font-bold">{m.value} reqs</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-2 bg-sonas-surface border-sonas-border rounded-3xl overflow-hidden shadow-sm">
          <div className="max-h-[400px] overflow-y-auto sonas-scrollbar">
            <table className="w-full text-left">
              <thead className="bg-sonas-surface2/50 border-b border-sonas-border sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-4 text-[9px] font-bold uppercase tracking-widest text-sonas-dim">Date</th>
                  <th className="px-6 py-4 text-[9px] font-bold uppercase tracking-widest text-sonas-dim">Model</th>
                  <th className="px-6 py-4 text-[9px] font-bold uppercase tracking-widest text-sonas-dim">Volume</th>
                  <th className="px-6 py-4 text-[9px] font-bold uppercase tracking-widest text-sonas-dim text-right">Burn</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sonas-border">
                {Array.isArray(data) && data.slice(0, 10).map((row, i) => (
                  <tr key={i} className="hover:bg-sonas-surface2/30 transition-colors">
                    <td className="px-6 py-3 text-xs font-mono">{row.date}</td>
                    <td className="px-6 py-3 text-xs font-medium text-sonas-accent">{row.model}</td>
                    <td className="px-6 py-3 text-xs font-bold">{row.requests}</td>
                    <td className="px-6 py-3 text-xs font-mono text-right text-sonas-muted">${(row.cost || 0).toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PollinationsDailyUsage;